import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import enterVerificationCode from '#src/assets/user/enter_verification_code.svg';
import { Suspense } from '#src/atoms/Suspense/index.tsx';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { Bold } from '#src/atoms/typography/Bold/index.tsx';
import { BodyM, HeadlineM } from '#src/atoms/typography/index.tsx';
import type { GraphQLResult } from '#src/components/form/Form/index.tsx';
import { GraphqlForm } from '#src/components/form/Form/index.tsx';
import { errorMessages } from '#src/components/user/VerifyPhoneNumber/i18n.ts';
import { PHONE_VERIFICATION_CODE_LENGTH } from '#src/constants/verificationCode.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { glossary } from '#src/lib/glossary.ts';

import { VerificationCodeInput } from './VerificationCodeInput/index.tsx';

const messages = defineMessages({
  header: {
    defaultMessage: 'Enter the 4-digit code',
  },
  description: {
    defaultMessage: 'We have sent a code to <b>{phoneNumber}</b>',
  },
  didNotReceiveCode: {
    defaultMessage: "Didn't get a code? <link>Click to resend</link>",
  },
  cta: {
    defaultMessage: 'Verify code',
  },
});

type Error = NonNullable<GraphQLResult['errors']>[number];

type EnterVerificationCodeProps = {
  submitVerificationCode: (verificationCode: string) => Promise<Error[] | null>;
  resendVerificationCode: (phoneNumber: string) => Promise<Error[] | null>;
  onCancel: () => void;
  onSuccess?: () => void;
  phoneNumber: string;
};

const NotReceivedLink = styled(ButtonBase).attrs({
  type: 'button',
})`
  color: inherit;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  text-decoration: underline;
  vertical-align: baseline;
`;

const Root = styled(Vertical).attrs({ gap: 0, center: true })`
  padding: var(--triple-unit);
  text-align: center;
  width: 100%;
  justify-content: flex-start;
`;

const Info = styled.div`
  padding: var(--double-unit);
`;

const Form = styled(GraphqlForm)`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  margin: 0;
  width: 100%;
`;

const Buttons = styled.div`
  display: flex;
  gap: var(--unit);
  justify-content: space-between;
  margin: auto;
  padding-top: var(--unit);
  width: 100%;
`;

export const EnterVerificationCode = ({
  submitVerificationCode,
  phoneNumber,
  resendVerificationCode,
  onCancel,
  onSuccess,
}: EnterVerificationCodeProps) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [disabled, setDisabled] = useState<boolean>(true);
  const track = useEvents();

  useEffect(() => {
    track('Show Enter Verification Code', {});
  }, [track]);

  const submitVerificationCodeCb = useCallback(
    (_vars: any, onResult: (result: GraphQLResult) => void) => {
      submitVerificationCode(verificationCode).then(errors => {
        onResult({ errors });
      });
    },
    [verificationCode, submitVerificationCode]
  );
  const resend = useCallback(() => {
    setDisabled(true);
    resendVerificationCode(phoneNumber).finally(() => {
      setDisabled(false);
    });
  }, [phoneNumber, resendVerificationCode]);

  useEffect(() => {
    setDisabled(verificationCode.length === 0);
  }, [verificationCode]);

  return (
    <Root>
      <img src={enterVerificationCode} alt="Enter received verification code" />
      <Info>
        <HeadlineM as="h4">
          <FormattedMessage {...messages.header} />
        </HeadlineM>
        <BodyM color="var(--c-nd-600)">
          <FormattedMessage
            values={{
              b: Bold,
              phoneNumber,
            }}
            {...messages.description}
          />
        </BodyM>
      </Info>
      <Suspense fallback={<LoadingIndicator />}>
        <Form
          autoComplete
          onSubmit={submitVerificationCodeCb}
          onSuccess={() => {
            if (onSuccess) onSuccess();
          }}
          onChange={(values, submit) => {
            if (verificationCode?.length === PHONE_VERIFICATION_CODE_LENGTH)
              submit();
          }}
          errorMessages={errorMessages}
          render={(Error, SubmitButton) => (
            <>
              <Error />
              <VerificationCodeInput
                value={verificationCode}
                onChange={setVerificationCode}
              />
              <BodyM color="var(--c-nd-600)">
                <FormattedMessage
                  values={{
                    link: chunks => (
                      <NotReceivedLink onClick={resend}>
                        {chunks}
                      </NotReceivedLink>
                    ),
                  }}
                  {...messages.didNotReceiveCode}
                />
              </BodyM>
              <Buttons>
                <Button
                  size="medium"
                  fullWidth
                  color="quaternary"
                  onClick={onCancel}
                >
                  <FormattedMessage {...glossary.cancel} />
                </Button>
                <SubmitButton fullWidth size="medium" disabled={disabled}>
                  <FormattedMessage {...messages.cta} />
                </SubmitButton>
              </Buttons>
            </>
          )}
        />
      </Suspense>
    </Root>
  );
};
